/**
 * Onboard Progress Indicator
 *
 * Visual step progress indicator for the onboard flow.
 * Shows numbered circles with step titles on desktop,
 * compact dots on mobile. Completed steps are clickable
 * and navigate back to that step's route.
 *
 * @see {@link ./__tests__/onboard-progress.test.tsx} for tests
 * @see {@link ../lib/constants.ts} for step configuration
 */

import { useNavigate } from 'react-router'
import { Check } from 'lucide-react'
import { cn } from '@bnsn/ui/lib/utils'
import { ONBOARD_STEPS } from '../lib/constants'
import type { OnboardStep, OnboardStepConfig } from '../lib/types'

interface OnboardProgressProps {
  currentStep: OnboardStep
  /** The furthest step completed via form submission (from session).
   *  Steps up to and including this are shown as blue with checkmarks. */
  completedStep?: OnboardStep
  /** The furthest step the user has visited (client-side high-water mark).
   *  Steps between completedStep and visitedStep are gray but clickable. */
  visitedStep?: OnboardStep
}

/**
 * Returns the numeric step index (1-based) for a given step ID.
 */
const getStepNumber = (stepId: OnboardStep): number => {
  const config = ONBOARD_STEPS.find((s) => s.id === stepId)
  return config?.step ?? 1
}

/**
 * Handles navigation to a step's path.
 * External URLs (e.g. pricing) use window.location.href,
 * internal paths use Remix navigate.
 */
const navigateToStep = (step: OnboardStepConfig, navigate: ReturnType<typeof useNavigate>): void => {
  if (step.path.startsWith('http')) {
    window.location.href = step.path
  } else {
    navigate(step.path)
  }
}

/**
 * Onboard Progress Component
 *
 * Uses a two-row layout: circles+lines on top, labels below.
 * Labels are positioned absolutely so they can center under
 * each circle regardless of text width.
 * Completed steps are clickable buttons that navigate to that step.
 */
export const OnboardProgress = ({ currentStep, completedStep, visitedStep }: OnboardProgressProps): JSX.Element => {
  const currentStepNumber = getStepNumber(currentStep)
  const completedStepNumber = completedStep ? getStepNumber(completedStep) : currentStepNumber
  const visitedStepNumber = visitedStep ? getStepNumber(visitedStep) : completedStepNumber
  const completedThreshold = Math.max(currentStepNumber, completedStepNumber)
  const visitedThreshold = Math.max(completedThreshold, visitedStepNumber)
  const navigate = useNavigate()

  return (
    <div className="w-full max-w-3xl mx-auto mb-4">
      {/* Circles + connector lines */}
      <div className="flex items-center">
        {ONBOARD_STEPS.map((step, index) => {
          const isCurrent = step.id === currentStep
          const isCompleted = step.step <= completedThreshold && !isCurrent
          const isVisited = !isCompleted && !isCurrent && step.step <= visitedThreshold
          const isClickable = isCompleted || isVisited
          const isFuture = !isCompleted && !isVisited && !isCurrent
          const isLast = index === ONBOARD_STEPS.length - 1

          const circleContent = isCompleted ? <Check className="h-4 w-4" data-testid={`step-check-${step.id}`} /> : step.step

          const circleClasses = cn(
            'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors shrink-0',
            isCompleted && 'bg-blue-600 text-white',
            isCurrent && 'bg-blue-600 text-white ring-2 ring-blue-200',
            (isVisited || isFuture) && 'bg-white text-slate-400 border border-slate-300',
          )

          const labelClasses = cn(
            'hidden sm:block absolute top-full mt-1 text-xs font-medium whitespace-nowrap left-1/2 -translate-x-1/2 text-center',
            isCurrent && 'text-blue-600',
            isCompleted && 'text-slate-600',
            (isVisited || isFuture) && 'text-slate-400',
          )

          return (
            <div key={step.id} className={cn('flex items-center', !isLast && 'flex-1')}>
              {/* Circle + label wrapper — relative so label can center under circle */}
              <div className="relative flex flex-col items-center">
                {isClickable ? (
                  <button
                    type="button"
                    data-testid={`step-indicator-${step.id}`}
                    data-state={isCompleted ? 'completed' : 'visited'}
                    aria-label={`Go to ${step.title}`}
                    onClick={() => navigateToStep(step, navigate)}
                    className={cn(circleClasses, 'cursor-pointer hover:ring-2 hover:ring-blue-300 focus:outline-blue-400')}
                  >
                    {circleContent}
                  </button>
                ) : (
                  <div
                    data-testid={`step-indicator-${step.id}`}
                    data-state={isCompleted ? 'completed' : isCurrent ? 'current' : 'future'}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={circleClasses}
                  >
                    {circleContent}
                  </div>
                )}
                {/* Label — absolutely positioned, centered under circle */}
                <span className={labelClasses}>{step.title}</span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className={cn('flex-1 h-0.5 mx-2', step.step < completedThreshold ? 'bg-blue-600' : 'bg-slate-300')} />
              )}
            </div>
          )
        })}
      </div>
      {/* Spacer for the absolutely positioned labels */}
      <div className="hidden sm:block h-5" />
    </div>
  )
}
